import React, { useEffect, useState, useMemo } from "react";
import Layout from "../../components/Layout/Layout";
import {
  SearchIcon,
  PlusIcon,
  RefreshIcon,
  PencilIcon,
  TrashIcon,
} from "@heroicons/react/solid";
import Spinner from "../../utils/Spinner";
import Paginate from "../../utils/Paginate";
import TransactionUtilitiesServices from "../../services/Transaction_Utilities";
import ConfirmModal from "./confirmModal";
import BankAvailabilityModal from "./bankAvailabilityModal";
import { toast } from "react-toastify";

const statusStyles = {
  Available: "bg-green-500 text-white",
  NotAvailable: "bg-red-600 text-white",
};

const statusOptions = [
  { value: "", label: "All Status" },
  { value: "Available", label: "Available" },
  { value: "NotAvailable", label: "Not Available" },
];




export function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export const TransactionUtilities = () => {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [pageIndex, setPageIndex] = useState(1);
  const [prevPage, setPrev] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPage, setTotalPage] = useState(0);
  const [nextPage, setNextPage] = useState(false);
  const [availabilityStatus, setAvailabilityStatus] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedBank, setSelectedBank] = useState(null);
  const [toggleLoading, setToggleLoading] = useState(null);

  const emptyInitialData = useMemo(() => ({
  bankCode: "",
  bankName: "",
  status: "Available",
  reason: "",
}), []);

  const fetchBankAvailability = async () => {
    setLoading(true);
    try {
      const response =
        await TransactionUtilitiesServices.listTransactionUtilities(
          10,
          pageIndex,
          search,
          availabilityStatus
        );

      if (response?.data) {
        const formattedBanks = response.data.result.map((bank) => ({
          ...bank,
          availabilityStatus: bank.status,
          lastUpdated: bank.lastStatusChanged,
        }));

        setBanks(formattedBanks || []);
        setPageNumber(response.data.totalCount);
        setTotalPage(response.data.totalPages);
        setPrev(response.data.hasPreviousPage);
        setNextPage(response.data.hasNextPage);
      } else {
        setBanks([]);
        toast.error("Failed to fetch bank availability data");
      }
    } catch (error) {
      console.error("Error fetching bank availability:", error);
      setBanks([]);
      toast.error(error.message || "Failed to fetch bank availability data");
    } finally {
      setLoading(false);
    }
  };
  const handleToggleStatus = async (bank) => {
    if (bank.availabilityStatus === "Deleted") {
      toast.error("Cannot toggle status of a deleted bank");
      return;
    }

    setToggleLoading(bank.bankAvailabilityId);
    try {
      const newStatus =
        bank.availabilityStatus === "Available" ? "NotAvailable" : "Available";

      const response =
        await TransactionUtilitiesServices.toggleBankAvailability(
          bank.bankAvailabilityId,
          {
            bankAvailabilityStatus: newStatus,
            reason: `Status toggled to ${newStatus}`,
          }
        );

      if (response?.status) {
        toast.success(response.data.message || "Status toggled successfully");
        await fetchBankAvailability();
      } else {
        toast.error(response?.data?.message || "Failed to toggle status");
      }
    } catch (error) {
      toast.error(error.message || "An error occurred");
    } finally {
      setToggleLoading(null);
    }
  };

  const handleDelete = async (reason) => {
    if (!selectedBank) return;

    setLoading(true);
    try {
      const response =
        await TransactionUtilitiesServices.deleteBankAvailability(
          selectedBank.bankAvailabilityId,
          { reason }
        );

      // Check for both 200 and 201 status codes
      if (response?.status) {
        toast.success(response.message || "Bank deleted successfully");
        await fetchBankAvailability();
        setShowDeleteModal(false);
      } else {
        toast.error(response?.data?.message || "Failed to delete");
      }
    } catch (error) {
      toast.error("error", "Error", error.message || "An error occurred");
    } finally {
      setShowDeleteModal(false);
      setLoading(false);
    }
  };

  const handleCreate = async (formData) => {
    setLoading(true);
    try {
      const response =
        await TransactionUtilitiesServices.createBankAvailability(formData);

      // Check for both 200 and 201 status codes
      if (response?.status) {
        toast.success(
          response.message || "Bank availability created successfully"
        );
        await fetchBankAvailability();
        setShowCreateModal(false);
      } else {
        toast.error(
          response?.data?.message || "Failed to create bank availability"
        );
      }
    } catch (err) {
      toast.error("error", "Error", err.message || "An error occurred");
    } finally {
      setShowCreateModal(false);
      setLoading(false);
    }
  };

  const handleUpdate = async (formData) => {
    if (!selectedBank) return;

    setLoading(true);
    try {
      const requestBody = {
        bankCode: formData.bankCode,
        bankName: formData.bankName,
        reason: formData.reason,
      };

      const response =
        await TransactionUtilitiesServices.updateBankAvailability(
          selectedBank.bankAvailabilityId,
          requestBody
        );

      if (response?.status) {
        toast.success(
          response.message || "Bank availability updated successfully"
        );
        await fetchBankAvailability();
        setShowEditModal(false);
      } else {
        toast.error(
          response?.data?.message || "Failed to update bank availability"
        );
      }
    } catch (err) {
      toast.error("error", "Error", err.message || "An error occurred");
    } finally {
      setShowEditModal(false);
      setLoading(false);
    }
  };

  const handleStatusChange = (status) => {
    setPageIndex(1);
    setAvailabilityStatus(status);
  };

  const handleSearch = (text) => {
    setPageIndex(1);
    setSearch(text);
  };

  const handlePageChange = (newPage) => {
    setPageIndex(newPage);
  };

  useEffect(() => {
    fetchBankAvailability();
  }, [pageIndex, search, availabilityStatus]);

  return (
    <Layout
      title="Transaction Utilities - Bank Availability"
      pageTag="transaction_utilities"
      current="/utilities"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        <div className="flex justify-between items-center mt-10">
          <h1 className="text-2xl font-bold text-gray-900">
            Bank Availability
          </h1>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Add New
          </button>
        </div>

        <div className="mt-10">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
            <div className="relative flex-grow">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SearchIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search by bank name or code"
                className="pl-10 block w-full sm:text-sm border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                onChange={(e) => handleSearch(e.target.value)}
                value={search}
              />
            </div>

            <div className="flex flex-col w-full sm:w-auto">
              <label className="text-sm text-gray-500 mb-1">
                Availability Status
              </label>
              <select
                value={availabilityStatus}
                onChange={(e) => handleStatusChange(e.target.value)}
                className="border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 h-full py-0 pl-2 pr-7"
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="mt-8 flex justify-center">
            <Spinner />
          </div>
        ) : banks.length === 0 ? (
          <div className="mt-8 text-center bg-white p-8 rounded-lg shadow">
            <div className="text-gray-500 text-lg">
              {search || availabilityStatus
                ? "No banks match your search criteria"
                : "No bank availability records found"}
            </div>
            {!search && !availabilityStatus && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
              >
                <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                Add New Bank
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="mt-8 overflow-x-auto">
              <div className="inline-block min-w-full align-middle">
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          Bank Name
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          Bank Code
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          Status
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          Last Updated
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          Reason
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {banks.map((bank) => (
                        <tr key={bank.bankAvailabilityId}>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {bank.bankName}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {bank.bankCode}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            <span
                              className={classNames(
                                statusStyles[bank.availabilityStatus] ||
                                  "bg-gray-200 text-gray-800",
                                "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                              )}
                            >
                              {bank.availabilityStatus}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {new Date(bank.lastUpdated).toLocaleString()}
                          </td>
                          <td className="px-3 py-4 text-sm text-gray-500 max-w-xs truncate">
                            {bank.reason || "N/A"}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleToggleStatus(bank)}
                                disabled={
                                  toggleLoading === bank.bankAvailabilityId
                                }
                                className="p-1 text-blue-600 hover:text-blue-800 disabled:opacity-50"
                                title="Toggle Status"
                              >
                                {toggleLoading === bank.bankAvailabilityId ? (
                                  <RefreshIcon className="h-5 w-5 animate-spin" />
                                ) : (
                                  <RefreshIcon className="h-5 w-5" />
                                )}
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedBank({
                                    ...bank,
                                    availabilityStatus: bank.status,
                                  });
                                  setShowEditModal(true);
                                }}
                                className="p-1 text-green-600 hover:text-green-800"
                                title="Edit"
                              >
                                <PencilIcon className="h-5 w-5" />
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedBank(bank);
                                  setShowDeleteModal(true);
                                }}
                                className="p-1 text-red-600 hover:text-red-800"
                                title="Delete"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <Paginate
              nextPage={nextPage}
  pageIndex={pageIndex}
  totalCount={pageNumber}
  totalPages={totalPage}
  prevPage={prevPage}
  setPageIndex={handlePageChange}
  loading={loading}
            />
          </>
        )}

        {/* Create Modal */}
        <BankAvailabilityModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreate}
          title="Add New Bank Availability"
          loading={loading}
          initialData={emptyInitialData}
        />

        {/* Edit Modal */}
        {selectedBank && (
          <BankAvailabilityModal
            isOpen={showEditModal}
            onClose={() => setShowEditModal(false)}
            onSubmit={handleUpdate}
            title="Edit Bank Availability"
            initialData={{
              bankCode: selectedBank.bankCode,
              bankName: selectedBank.bankName,
              status: selectedBank.availabilityStatus || selectedBank.status,
              reason: selectedBank.reason || "",
            }}
            loading={loading}
            isEditMode={true}
          />
        )}

        {/* Delete Confirmation Modal */}
        <ConfirmModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDelete}
          title="Confirm Delete"
          message={`Are you sure you want to set delete to ${selectedBank?.bankName}?`}
          confirmText="Delete"
          cancelText="Cancel"
          inputLabel="Reason for deletion"
          inputPlaceholder="Enter reason for deletion"
          inputRequired={true}
          loading={loading}
        />
      </div>
    </Layout>
  );
};

export default TransactionUtilities;
